'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/context/AuthContext';

/**
 * Hydration-safe version of useAuth.
 *
 * Problem: AuthContext reads from cache on client, which can differ from server state,
 * causing hydration mismatches (server renders logged-out, client hydrates logged-in).
 *
 * Solution: This hook returns a consistent "loading" state during SSR and first client
 * render (hydration), then switches to real auth state after mount.
 *
 * Use this hook in any component that:
 * 1. Conditionally renders based on user/profile
 * 2. Needs to work with SSR (pages, not just client-only modals)
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   const { user, profile, loading } = useHydrationSafeAuth();
 *
 *   if (loading) return <LoadingSpinner />;
 *   if (!user) return <LoginPrompt />;
 *
 *   return <AuthenticatedContent user={user} />;
 * }
 * ```
 */
export function useHydrationSafeAuth() {
  const [mounted, setMounted] = useState(false);
  const auth = useAuth();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, []);

  // During SSR and hydration, return loading state to prevent mismatch
  if (!mounted) {
    return {
      user: null,
      profile: null,
      loading: true,
      profileLoading: false,
      signOut: auth.signOut,
      signOutAllDevices: auth.signOutAllDevices,
      refreshProfile: auth.refreshProfile,
    };
  }

  // After mount, return actual auth state
  return auth;
}
